import { useEffect } from "react"
import { Link, useLocation } from "react-router-dom"
import { Wordmark } from "../components/brand"
import { Markdown } from "../components/markdown"
import { ErrorNote, Spinner, useLoad } from "../components/ui"
import { api } from "../lib/api"
import { DownloadsSection } from "./downloads"

// The public docs: the getting-started guide the server also seeds into every
// company's Context Hub, then the downloads. Public — no session — because the
// people sent here have none yet: a colleague told to install the CLI, or a
// CLI update prompt linking to it.
//
// The text comes from /api/docs rather than being written here so the wiki
// page and this one cannot drift.

// Markdown headings get an id from their text so a link can land on a section:
// the CLI's "Help" links point at /docs#getting-help, the app's downloads
// button at /docs#downloads.
function slug(text: string) {
  return text.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "")
}

export function DocsPage() {
  const docs = useLoad(() => api.get<{ markdown: string }>("/docs"))
  const { hash } = useLocation()

  useEffect(() => {
    if (!docs.data) return
    for (const heading of document.querySelectorAll<HTMLElement>("main h2, main h3")) {
      if (!heading.id) heading.id = slug(heading.textContent ?? "")
    }
    if (hash) document.getElementById(hash.slice(1))?.scrollIntoView()
  }, [docs.data, hash])

  return (
    <div className="flex min-h-full justify-center px-4 py-10">
      <main className="flex w-full max-w-3xl flex-col gap-10">
        <header className="flex items-center justify-between gap-4">
          <Link to="/docs" aria-label="Moderniser docs">
            <Wordmark className="h-5 w-auto text-foreground" />
          </Link>
          <Link to="/" className="text-sm text-muted hover:text-foreground">
            Open Moderniser →
          </Link>
        </header>

        {docs.loading && <Spinner label="Loading the docs" />}
        <ErrorNote error={docs.error} />
        {docs.data && <Markdown className="[&_h1]:text-2xl [&_h2]:scroll-mt-6">{docs.data.markdown}</Markdown>}

        <DownloadsSection />
      </main>
    </div>
  )
}
